import { traducirError, useTraduccion, type ProblemaApi } from '@tallerya/i18n';
import { AvisoError, Boton, Chip, useAvisos, Ventana, type AvisoFormulario } from '@tallerya/ui';
import { useRef, useState, type DragEvent } from 'react';
import {
  bajarPlantilla,
  revisarImportacion,
  TOPE_IMPORTACION,
  useCambiosItem,
  type FormatoPlantilla,
  type ResultadoImportacion,
  type RevisionImportacion,
} from './datos';

// «Importar (1. Subir archivo)» e «Importar (2. Revisar)» del Figma (#114).
//
// La API no guarda nada entre los dos pasos: el 1 sube el archivo y recibe la
// revisión, y el 2 lo sube **otra vez** para importarlo. Por eso la ventana se
// queda con el `File` y no con un id: la API lo vuelve a revisar contra la base
// del momento, y lo que cambió entre medias sale como problema (ver
// `CatalogImport.cs`).

interface Props {
  abierta: boolean;
  onCerrar: () => void;
}

type Estado =
  | { paso: 'subir'; revisando: string | null }
  | { paso: 'revisar'; archivo: File; revision: RevisionImportacion }
  // Importó, pero filas que estaban bien en la revisión quedaron afuera: se
  // muestran, y ya no se ofrece importar —lo importado entraría dos veces—.
  | { paso: 'importado'; resultado: ResultadoImportacion; cambiaron: number };

export function ImportarItems({ abierta, onCerrar }: Props) {
  // Cada vez que se abre, desde el paso 1.
  return abierta ? <Contenido onCerrar={onCerrar} /> : null;
}

function Contenido({ onCerrar }: Omit<Props, 'abierta'>) {
  const { t } = useTraduccion();
  const { avisar } = useAvisos();
  const cambios = useCambiosItem();
  const selector = useRef<HTMLInputElement>(null);

  const [estado, setEstado] = useState<Estado>({ paso: 'subir', revisando: null });
  const [error, setError] = useState<AvisoFormulario | null>(null);
  const [importando, setImportando] = useState(false);
  const [encima, setEncima] = useState(false);

  // El título dice qué falló —revisar, importar—; el texto, por qué. Sin código
  // no hubo una respuesta que entender.
  const tituloRevisar = t('admin.catalogo.importar.error.titulo');
  const tituloImportar = t('admin.catalogo.importar.error.importar');
  const fallo = (titulo: string, problema?: ProblemaApi) =>
    setError({
      titulo,
      texto: problema?.code ? traducirError(problema.code, t) : t('admin.catalogo.error.reintentar'),
    });

  /** Lo que se puede saber sin subirlo. El resto —que se lea, las columnas— lo dice la API. */
  function problemaDelArchivo(archivo: File): string | null {
    if (!/\.(xlsx|csv)$/i.test(archivo.name)) return t('admin.catalogo.importar.tipoInvalido');
    if (archivo.size === 0) return traducirError('catalog_import.archivo_vacio', t);
    if (archivo.size > TOPE_IMPORTACION) return t('admin.catalogo.importar.muyGrande');
    return null;
  }

  async function revisar(archivo: File) {
    setError(null);
    const problema = problemaDelArchivo(archivo);
    if (problema) {
      setError({ titulo: tituloRevisar, texto: problema });
      return;
    }

    setEstado({ paso: 'subir', revisando: archivo.name });
    try {
      // Una copia en memoria: el paso 2 sube exactamente lo que se revisó. El
      // `File` del selector apunta al disco, y si se corrige en Excel entre los
      // dos pasos, Chromium falla la subida y otros suben un archivo distinto.
      const fijo = new File([await archivo.arrayBuffer()], archivo.name, { type: archivo.type });
      const respuesta = await revisarImportacion(fijo);
      if (respuesta.revision) {
        setEstado({ paso: 'revisar', archivo: fijo, revision: respuesta.revision });
        return;
      }
      setEstado({ paso: 'subir', revisando: null });
      fallo(tituloRevisar, respuesta.problema);
    } catch {
      setEstado({ paso: 'subir', revisando: null });
      fallo(tituloRevisar);
    }
  }

  function alSoltar(evento: DragEvent<HTMLDivElement>) {
    evento.preventDefault();
    setEncima(false);
    const archivo = evento.dataTransfer.files[0];
    if (archivo && estado.paso === 'subir' && !estado.revisando) void revisar(archivo);
  }

  async function plantilla(formato: FormatoPlantilla) {
    setError(null);
    try {
      await bajarPlantilla(formato);
    } catch {
      setError({ titulo: t('admin.catalogo.error.titulo'), texto: t('admin.catalogo.importar.plantillaError') });
    }
  }

  /**
   * Lo que pasa cuando la importación respondió bien. `resultado.problems` son
   * las filas que quedaron afuera **al importar**: las de la revisión, más las que
   * cambiaron desde entonces —un código que alguien dio de alta entre los dos pasos—.
   */
  function terminar(resultado: ResultadoImportacion) {
    if (estado.paso !== 'revisar') return;
    const importados = Number(resultado.importedCount);
    avisar({ titulo: t('admin.catalogo.importar.hecho', { n: importados }) });

    // Las que ya salían en la revisión se vieron antes de importar: no hace falta
    // quedarse. Solo las nuevas son una sorpresa que hay que mostrar.
    const conocidas = new Set(estado.revision.problems.map((p) => Number(p.row)));
    const cambiaron = new Set(resultado.problems.map((p) => Number(p.row)).filter((f) => !conocidas.has(f))).size;
    if (cambiaron === 0) onCerrar();
    else setEstado({ paso: 'importado', resultado, cambiaron });
  }

  async function importar() {
    if (estado.paso !== 'revisar') return;
    setError(null);
    setImportando(true);
    try {
      const { resultado, problema } = await cambios.importar(estado.archivo);
      if (resultado) terminar(resultado);
      else fallo(tituloImportar, problema);
    } catch {
      fallo(tituloImportar);
    } finally {
      setImportando(false);
    }
  }

  const cancelar = (
    <Boton variante="texto" disabled={importando} onClick={onCerrar}>
      {t('admin.catalogo.importar.cancelar')}
    </Boton>
  );

  const aviso = error && <AvisoError aviso={error} />;

  if (estado.paso === 'subir') {
    return (
      <Ventana
        abierta
        ancho={560}
        titulo={t('admin.catalogo.importar.titulo')}
        info={`${t('admin.catalogo.importar.descripcion')} ${t('admin.catalogo.importar.columnas')}`}
        acciones={cancelar}
      >
        {aviso}
        <p className="importar__plantilla">
          {t('admin.catalogo.importar.plantilla')}{' '}
          <Boton variante="texto" onClick={() => void plantilla('xlsx')}>
            {t('admin.catalogo.exportar.excel')}
          </Boton>
          <Boton variante="texto" onClick={() => void plantilla('csv')}>
            {t('admin.catalogo.exportar.csv')}
          </Boton>
        </p>
        <div
          className={`importar__zona${encima ? ' importar__zona--encima' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setEncima(true);
          }}
          onDragLeave={() => setEncima(false)}
          onDrop={alSoltar}
          aria-busy={estado.revisando !== null}
        >
          {estado.revisando ? (
            <p>{t('admin.catalogo.importar.revisando', { archivo: estado.revisando })}</p>
          ) : (
            <>
              <p>
                {t('admin.catalogo.importar.zona')}{' '}
                <Boton variante="texto" onClick={() => selector.current?.click()}>
                  {t('admin.catalogo.importar.elegir')}
                </Boton>
              </p>
              <small>{t('admin.catalogo.importar.formatos')}</small>
            </>
          )}
          <input
            ref={selector}
            type="file"
            hidden
            accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            onChange={(e) => {
              const archivo = e.target.files?.[0];
              // Vaciarlo deja volver a elegir el mismo archivo después de corregirlo.
              e.target.value = '';
              if (archivo) void revisar(archivo);
            }}
          />
        </div>
      </Ventana>
    );
  }

  if (estado.paso === 'importado') {
    return (
      <Ventana
        abierta
        ancho={640}
        titulo={t('admin.catalogo.importar.titulo')}
        descripcion={`${t('admin.catalogo.importar.hecho', { n: Number(estado.resultado.importedCount) })}. ${t(
          'admin.catalogo.importar.cambiaron',
          { n: estado.cambiaron },
        )}`}
        acciones={<Boton onClick={onCerrar}>{t('admin.catalogo.importar.cerrar')}</Boton>}
      >
        <TablaProblemas problemas={estado.resultado.problems} />
      </Ventana>
    );
  }

  const { archivo, revision } = estado;
  const listos = Number(revision.readyCount);
  // Una fila puede traer varios problemas: se cuentan filas, no problemas.
  const conError = new Set(revision.problems.map((p) => Number(p.row))).size;

  return (
    <Ventana
      abierta
      ancho={640}
      titulo={t('admin.catalogo.importar.titulo')}
      descripcion={t('admin.catalogo.importar.leidas', { n: listos + conError, archivo: archivo.name })}
      izquierda={
        <Boton
          variante="texto"
          disabled={importando}
          onClick={() => {
            setError(null);
            setEstado({ paso: 'subir', revisando: null });
          }}
        >
          {t('admin.catalogo.importar.otro')}
        </Boton>
      }
      acciones={
        <>
          {cancelar}
          <Boton disabled={listos === 0 || importando} aria-busy={importando} onClick={() => void importar()}>
            {t('admin.catalogo.importar.boton', { n: listos })}
          </Boton>
        </>
      }
    >
      {aviso}
      <p className="importar__resumen">
        <Chip tono="exito">{t('admin.catalogo.importar.listos', { n: listos })}</Chip>
        {conError > 0 && <Chip>{t('admin.catalogo.importar.conErrores', { n: conError })}</Chip>}
      </p>
      {revision.newCategories.length > 0 && (
        <p className="ventana-nota">
          {t('admin.catalogo.importar.nuevasCategorias', { lista: revision.newCategories.join(', ') })}
        </p>
      )}
      {listos === 0 && <p className="ventana-nota">{t('admin.catalogo.importar.nada')}</p>}
      {revision.problems.length > 0 && <TablaProblemas problemas={revision.problems} />}
    </Ventana>
  );
}

function TablaProblemas({ problemas }: { problemas: RevisionImportacion['problems'] }) {
  const { t } = useTraduccion();
  return (
    <div className="importar__tabla">
      <table>
        <thead>
          <tr>
            <th>{t('admin.catalogo.importar.columna.fila')}</th>
            <th>{t('admin.catalogo.importar.columna.nombre')}</th>
            <th>{t('admin.catalogo.importar.columna.problema')}</th>
          </tr>
        </thead>
        <tbody>
          {problemas.map((p, i) => (
            // Una fila con dos problemas repite número: el índice desempata.
            <tr key={`${String(p.row)}-${p.code}-${String(i)}`}>
              <td>{String(p.row)}</td>
              <td>{p.name ?? '—'}</td>
              <td>{traducirError(p.code, t)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
